/**
 * Service worker registration, the install prompt (docs/52 S04) and push subscription (docs/50 §4.9).
 * iPhone push needs the app on the home screen and a tap before asking (WebKit), so push is always the fast lane
 * and email carries every notification that matters.
 */
import { call } from '../lib/api.ts';
import { createStore } from '../lib/store.ts';

export const updateReady = createStore<ServiceWorker | null>(null);

interface TrustedTypesLike {
  createPolicy(name: string, rules: { createScriptURL(url: string): string }): { createScriptURL(url: string): unknown };
}

/**
 * The CSP enforces Trusted Types (`require-trusted-types-for 'script'`), and Chrome treats the service worker URL as
 * a script URL: a plain string is blocked, so the app would never install or work offline. The one policy the CSP
 * allows (`trusted-types ff-sw`, public/_headers) passes exactly `/sw.js` and refuses anything else.
 */
function swUrl(): string {
  const tt = (globalThis as { trustedTypes?: TrustedTypesLike }).trustedTypes;
  if (!tt) return '/sw.js';
  const policy = tt.createPolicy('ff-sw', {
    createScriptURL(url) {
      if (url !== '/sw.js') throw new TypeError('only /sw.js may be registered');
      return url;
    },
  });
  return policy.createScriptURL('/sw.js') as string;
}

export function registerServiceWorker(): void {
  if (!('serviceWorker' in navigator) || import.meta.env.DEV) return;
  window.addEventListener('load', async () => {
    try {
      const reg = await navigator.serviceWorker.register(swUrl(), { scope: '/' });
      const watch = (w: ServiceWorker | null) => {
        if (!w) return;
        w.addEventListener('statechange', () => {
          if (w.state === 'installed' && navigator.serviceWorker.controller) updateReady.set(w);
        });
      };
      if (reg.waiting && navigator.serviceWorker.controller) updateReady.set(reg.waiting);
      reg.addEventListener('updatefound', () => watch(reg.installing));
      let reloaded = false;
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        if (reloaded) return;
        reloaded = true;
        window.location.reload();
      });
    } catch {
      /* the app works without a service worker, just not offline */
    }
  });
}

export function applyUpdate(): void {
  updateReady.get()?.postMessage({ type: 'skip-waiting' });
}

// ------------------------------------------------------------------------------------------ install

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export const installEvent = createStore<BeforeInstallPromptEvent | null>(null);
if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    installEvent.set(e as BeforeInstallPromptEvent);
  });
  window.addEventListener('appinstalled', () => {
    installEvent.set(null);
    rememberInstall('installed');
  });
}

export function isStandalone(): boolean {
  return window.matchMedia?.('(display-mode: standalone)').matches || (navigator as Navigator & { standalone?: boolean }).standalone === true;
}

export type Platform = 'ios' | 'android' | 'desktop';
export function platform(): Platform {
  const ua = navigator.userAgent;
  if (/iPhone|iPad|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)) return 'ios';
  if (/Android/.test(ua)) return 'android';
  return 'desktop';
}

const INSTALL_KEY = 'ff-install';
interface InstallMemo {
  dismissed: number;
  lastShown: number;
  installed?: boolean;
}
function memo(): InstallMemo {
  try {
    return { dismissed: 0, lastShown: 0, ...(JSON.parse(localStorage.getItem(INSTALL_KEY) ?? '{}') as Partial<InstallMemo>) };
  } catch {
    return { dismissed: 0, lastShown: 0 };
  }
}
function rememberInstall(what: 'dismissed' | 'shown' | 'installed') {
  const m = memo();
  if (what === 'dismissed') m.dismissed += 1;
  if (what === 'shown') m.lastShown = Date.now();
  if (what === 'installed') m.installed = true;
  try {
    localStorage.setItem(INSTALL_KEY, JSON.stringify(m));
  } catch {
    /* private mode */
  }
}
export const noteInstallDismissed = () => rememberInstall('dismissed');
export const noteInstallShown = () => rememberInstall('shown');

/** Once a week from Home until installed or dismissed twice (docs/52 S04). */
export function shouldOfferInstall(now = Date.now()): boolean {
  if (typeof window === 'undefined' || isStandalone()) return false;
  const m = memo();
  if (m.installed || m.dismissed >= 2) return false;
  return now - m.lastShown > 7 * 86_400_000;
}

// ------------------------------------------------------------------------------------------ push

export type PushState = 'unsupported' | 'needs_install' | 'off' | 'denied' | 'on';

const SUB_KEY = 'ff-push-sub';
const VAPID = (import.meta.env.VITE_VAPID_PUBLIC_KEY as string | undefined) ?? '';

export async function pushState(): Promise<PushState> {
  if (!('Notification' in window) || !('serviceWorker' in navigator) || !('PushManager' in window)) {
    return platform() === 'ios' && !isStandalone() ? 'needs_install' : 'unsupported';
  }
  if (Notification.permission === 'denied') return 'denied';
  if (Notification.permission !== 'granted') return 'off';
  try {
    const reg = await navigator.serviceWorker.getRegistration();
    const sub = await reg?.pushManager.getSubscription();
    return sub ? 'on' : 'off';
  } catch {
    return 'off';
  }
}

function b64urlToBytes(s: string): Uint8Array<ArrayBuffer> {
  const pad = '='.repeat((4 - (s.length % 4)) % 4);
  const bin = atob((s + pad).replace(/-/g, '+').replace(/_/g, '/'));
  const out = new Uint8Array(new ArrayBuffer(bin.length));
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

/** Must run inside a tap (iOS requires a user gesture). */
export async function turnOnPush(): Promise<PushState> {
  const perm = await Notification.requestPermission();
  if (perm !== 'granted') return perm === 'denied' ? 'denied' : 'off';
  if (!VAPID) return 'off';
  const reg = await navigator.serviceWorker.ready;
  const sub = (await reg.pushManager.getSubscription()) ?? (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: b64urlToBytes(VAPID) }));
  const json = sub.toJSON() as { endpoint: string; keys: { p256dh: string; auth: string }; expirationTime?: number | null };
  const created = await call('pushSubscribe', { body: { endpoint: json.endpoint, keys: json.keys, expirationTime: json.expirationTime ?? null } });
  try {
    localStorage.setItem(SUB_KEY, created.id);
  } catch {
    /* private mode */
  }
  return 'on';
}

export async function turnOffPush(): Promise<PushState> {
  const reg = await navigator.serviceWorker.getRegistration();
  const sub = await reg?.pushManager.getSubscription();
  await sub?.unsubscribe();
  let id: string | null = null;
  try {
    id = localStorage.getItem(SUB_KEY);
    localStorage.removeItem(SUB_KEY);
  } catch {
    /* private mode */
  }
  if (id) await call('pushUnsubscribe', { params: { id } }).catch(() => undefined);
  return 'off';
}

/** The app icon badge on installed apps (Badging API). */
export function setBadge(n: number): void {
  const nav = navigator as Navigator & { setAppBadge?: (n?: number) => Promise<void>; clearAppBadge?: () => Promise<void> };
  if (n > 0) void nav.setAppBadge?.(n).catch(() => undefined);
  else void nav.clearAppBadge?.().catch(() => undefined);
}
